import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TextField
} from "@mui/material";
import { useEffect } from "react";
import { useState } from "react";
import { BoxColumn } from "../../components/common/Boxes";
import { shortenAddress } from "../../components/common/util";
import { withProtectedRoute } from "../../context/Authentication";
import {
  useGetAllTournamentsQuery,
  useEnterTournamentMutation,
  useGetAllTournamentsCategorizedQuery
} from "../../generated/graphql_schema";
import { LinkButton } from "../../components/common/LinkButton";
import { Modal } from "../../components/common/Modal";
import styles from "../../styles/Lobby.module.css";
import { useRouter } from "next/router";
import { useAlert } from "../../hooks/hooks";
import { Alert } from "../../components/Alert";

export interface ITournamentSettings {
  id: string;
  name: string;
  potencyCap?: number | null | undefined;
  requiredWins?: number | null | undefined;
  isTimerRequired?: boolean | null | undefined;
}

const TournamentPage = () => {
  const { data, loading, error, refetch } =
    useGetAllTournamentsCategorizedQuery();
  // const { data, loading, error, refetch } = useGetAllTournamentsQuery();
  const [tournamentSettingsModal, setTournamentSettingsModal] =
    useState<ITournamentSettings>();

  const [enterTournamentMutation, { loading: isLoadingCreateTournament }] =
    useEnterTournamentMutation();
  const router = useRouter();

  const [discordHandle, setDiscordHandle] = useState<string | null>(
    null
  );

  const openTournaments =
    data?.getAllTournamentsCategorized.openTournaments ?? [];
  const closedTournaments =
    data?.getAllTournamentsCategorized.closedTournaments ?? [];
  const activeTournaments =
    data?.getAllTournamentsCategorized.activeTournaments ?? [];

  const {
    messageError,
    setMessageError,
    messageSuccess,
    setMessageSuccess,
    unsetMessages
  } = useAlert();

  const handleCloseModal = () => {
    setTournamentSettingsModal(undefined);
  };

  const isTimerRequiredText = (
    isTimerRequiredSetting: boolean | null | undefined
  ): string => {
    switch (isTimerRequiredSetting) {
      case null:
        return "players' choice";
      case true:
        return "yes";
      case false:
        return "no";
      default:
        return "players' choice";
    }
  };

  const ModalContent = (
    <BoxColumn sx={{ p: 2, alignItems: "center" }}>
      <Typography variant="h3" sx={{ paddingBottom: 3 }}>
        Tournament Settings
      </Typography>
      <Typography variant="h5">
        Tournament Name: {tournamentSettingsModal?.name}
      </Typography>
      <Typography variant="h5">
        Potency Cap:{" "}
        {tournamentSettingsModal?.potencyCap
          ? tournamentSettingsModal?.potencyCap
          : "players' choice"}
      </Typography>
      <Typography variant="h5">
        Required Wins:{" "}
        {tournamentSettingsModal?.requiredWins
          ? tournamentSettingsModal?.requiredWins
          : "players' choice"}
      </Typography>
      <Typography variant="h5" sx={{ paddingBottom: 2 }}>
        Is Timer Required:{" "}
        {isTimerRequiredText(tournamentSettingsModal?.isTimerRequired)}
      </Typography>

      <Box
        sx={{
          paddingBottom: 2
        }}
      >
        <Box
          className="discord-handle"
          sx={{
            background: "rgb(0,0,0,1)",
            borderRadius: "6px",
            display: "flex",
            justifyContent: "center"
          }}
        >
          <TextField
            label="discord handle"
            color="primary"
            onChange={(t) =>
              setDiscordHandle(
                t.target.value
              )
            }
          />
        </Box>
      </Box>

      <Button
        sx={{ width: "fit-content" }}
        onClick={() => {
          enterTournamentMutation({
            variables: { tournamentId: tournamentSettingsModal!.id, discordHandle: discordHandle }
          });
          setMessageSuccess("success");
        }}
      >
        Join Tournament
      </Button>
    </BoxColumn>
  );

  useEffect(() => {
    // todo is there better way? maybe subscribe to tournament cud
    refetch();
  }, [refetch]);

  // sort by startTime
  const tournamentsSorted = [...(openTournaments || [])].sort((a, b) => {
    if (a?.startTime > b?.startTime) return 1;
    if (b?.startTime > a?.startTime) return -1;
    return 0;
  });

  const Loading = (
    <TableRow>
      <TableCell>
        <CircularProgress />
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <BoxColumn
        sx={{ p: 2, alignItems: "center", overflowY: "auto", height: "100%" }}
      >
        <Typography variant="h3">In Progress</Typography>
        <TableContainer
          sx={{
            width: 600,
            maxWidth: "100%",
            flex: "0 0 75%",
            maxHeight: "80vh",
            mb: 2
          }}
          className={styles.LargeFontParent}
        >
          <Table
            stickyHeader
            sx={{
              background: "rgb(0,0,0,1)",
              borderRadius: 1
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && Loading}
              {!loading &&
                (!activeTournaments ||
                  (!activeTournaments.length && (
                    <TableRow>
                      <TableCell colSpan={6}>no tournaments found</TableCell>
                    </TableRow>
                  )))}
              {activeTournaments
                ?.filter((t) => !t?.entrantsWithRecords.some((e) => e?.winner))
                .map((tournament) => (
                  <TableRow
                    key={tournament?.id}
                    hover
                    onClick={() =>
                      router.push(`/tournaments/${tournament?.name}`)
                    }
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{tournament?.name}</TableCell>
                    <TableCell>{tournament?.type}</TableCell>
                    <TableCell>
                      {new Date(tournament?.startTime).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {new Date(tournament?.endTime).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="h3">Open for Signup</Typography>
        <TableContainer
          sx={{
            width: 600,
            maxWidth: "100%",
            flex: "0 0 75%",
            maxHeight: "80vh",
            mb: 2
          }}
          className={styles.LargeFontParent}
        >
          <Table
            stickyHeader
            sx={{
              background: "rgb(0,0,0,1)",
              borderRadius: 1
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Start Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && Loading}
              {!loading &&
                (!tournamentsSorted ||
                  (!tournamentsSorted.length && (
                    <TableRow>
                      <TableCell colSpan={6}>no tournaments found</TableCell>
                    </TableRow>
                  )))}

              {tournamentsSorted?.map((tournament) => (
                <TableRow
                  key={tournament?.id}
                  hover
                  onClick={() =>
                    setTournamentSettingsModal({
                      id: tournament?.id ?? "0",
                      name: tournament?.name ?? "",
                      potencyCap: tournament?.tournamentSettings?.potency,
                      requiredWins:
                        tournament?.tournamentSettings?.requiredWins,
                      isTimerRequired:
                        tournament?.tournamentSettings?.timerRequired
                    })
                  }
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{tournament?.name}</TableCell>
                  <TableCell>{tournament?.type}</TableCell>
                  <TableCell>
                    {new Date(tournament?.startTime).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="h3">Completed</Typography>
        <TableContainer
          sx={{
            width: 600,
            maxWidth: "100%",
            flex: "0 0 75%",
            maxHeight: "80vh",
            mb: 2
          }}
          className={styles.LargeFontParent}
        >
          <Table
            stickyHeader
            sx={{
              background: "rgb(0,0,0,1)",
              borderRadius: 1
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && Loading}
              {!loading &&
                (!activeTournaments ||
                  (!activeTournaments.length && (
                    <TableRow>
                      <TableCell colSpan={6}>no tournaments found</TableCell>
                    </TableRow>
                  )))}

              {activeTournaments
                ?.filter((t) => t?.entrantsWithRecords.some((e) => e?.winner))
                .map((tournament) => (
                  <TableRow
                    key={tournament?.id}
                    hover
                    onClick={() =>
                      router.push(`/tournaments/${tournament?.name}`)
                    }
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{tournament?.name}</TableCell>
                    <TableCell>{tournament?.type}</TableCell>
                    <TableCell>
                      {new Date(tournament?.startTime).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {new Date(tournament?.endTime).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Modal
          open={Boolean(tournamentSettingsModal)}
          onClose={handleCloseModal}
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "fit-content",
            height: "fit-content",
            maxHeight: "fit-content"
          }}
        >
          {ModalContent}
        </Modal>
      </BoxColumn>
      <Alert
        onClose={unsetMessages}
        messageError={messageError}
        messageSuccess={messageSuccess}
      />
    </>
  );
};

export default TournamentPage;
